import React, {useContext, useEffect, useMemo, useRef, useState, useCallback} from 'react'
import './../style/chart/App.scss';

import {colors, gradientToColorTable} from './../services/chart/Functions';

import CanvasContent from "../components/chart/CanvasContent";
import {useSearchParams} from "react-router-dom";

import {ProgressSpinner} from "primereact/progressspinner";
import * as turf from '@turf/turf';
import {classNames} from "primereact/utils";
import {useAppDispatch, useAppSelector} from "../redux/hooks";
import {setColorTable, setColorTableXrf, setColorTableMinerals, setMinMax, setSelectLine, setDataSources, DataSource, setSelectedItem, setChartActiveOverrides, setColorGradientXrf, setColorGradientMinerals, setColorByElementXrf, setColorByElementMinerals, GradientStop} from "../redux/slices/ChartStateSlice";
import {elementKey, unitSuffix} from "../functions/drillholeTransform";
import {useDrillholeData} from "../components/hooks/useDrillholeData";
import {useDrillholeDetails} from "../components/hooks/useDrillholeDetails";
import {Input} from "@dmt/global_npm";
import ControlBar from "./global/ControlBar";

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {useTemplates} from "../components/hooks/useTemplates";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";


export default function Chart() {

    const {data, withCoords, isLoading: dataLoading} = useDrillholeData();
    const {fetchDetails} = useDrillholeDetails();
    const [selectedDrillholeIds, setSelectedDrillholeIds] = useState<string[]>(() => {
        try {
            const raw = localStorage.getItem('chartSelectedDrillholes');
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) return arr;
            }
        } catch (_) {
        }
        return [];
    });
    const isInitialMount = useRef(true);

    const minMax = useAppSelector((state: any) => state.oldDesignState.minMax);
    const selectedBoreholesRaw = useAppSelector((state: any) => state.oldDesignState.selectedBoreholes);
    const selectLine = useAppSelector((state: any) => state.oldDesignState.selectLine);
    const offset = useAppSelector((state: any) => state.oldDesignState.offset);
    const dataSources = useAppSelector((state: any) => state.oldDesignState.dataSources);
    const isControlBarCollapsed = useAppSelector((state: any) => state.oldDesignState.isControlBarCollapsed);
    const selectedItem = useAppSelector((state: any) => state.oldDesignState.selectedItem);
    const rawElements = useAppSelector((state: any) => state.oldDesignState.elements);
    const chartActiveOverrides = useAppSelector((state: any) => state.oldDesignState.chartActiveOverrides) as Record<string, boolean>;
    const colorGradientXrf = useAppSelector((state: any) => state.oldDesignState.colorGradientXrf) as GradientStop[];
    const colorGradientMinerals = useAppSelector((state: any) => state.oldDesignState.colorGradientMinerals) as GradientStop[];
    const colorByElementXrf = useAppSelector((state: any) => state.oldDesignState.colorByElementXrf) as boolean;
    const colorByElementMinerals = useAppSelector((state: any) => state.oldDesignState.colorByElementMinerals) as boolean;
    const elements = useMemo(
        () => rawElements.map((el: any) => {
            const key = elementKey(el);
            const overridden = Object.prototype.hasOwnProperty.call(chartActiveOverrides, key);
            return overridden ? {...el, active: !!chartActiveOverrides[key]} : el;
        }),
        [rawElements, chartActiveOverrides]
    );

    const {templates, addTemplate, updateTemplate, deleteTemplate} = useTemplates<{
        selectedDrillholeIds: string[],
        dataSources: DataSource[],
        // Legacy: templates saved before multi-source
        dataSource?: DataSource,
        heightFactor: number,
        camera: { position: [number, number, number], zoom: number, target: [number, number, number] } | null,
        hideInactive?: boolean,
        nnOffset?: number,
        activeOverrides?: Record<string, boolean>,
        colorGradientXrf?: GradientStop[],
        colorGradientMinerals?: GradientStop[],
        colorByElementXrf?: boolean,
        colorByElementMinerals?: boolean,
        boreholeWidth?: number,
    }>('chart_templates');

    const [legendHideInactive, setLegendHideInactive] = useState<boolean>(() => {
        return localStorage.getItem('chartLegendHideInactive') === 'true';
    });
    const [nnOffset, setNnOffset] = useState<number>(() => {
        const raw = localStorage.getItem('chartNNOffset');
        if (raw == null) return 0;
        const parsed = Number(raw);
        return isFinite(parsed) ? parsed : 0;
    });
    const [boreholeWidth, setBoreholeWidth] = useState<number>(() => {
        const raw = localStorage.getItem('chartBoreholeWidth');
        if (raw == null) return 7;
        const parsed = Number(raw);
        return isFinite(parsed) && parsed > 0 ? parsed : 7;
    });
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() => {
        return localStorage.getItem('chartSelectedTemplateId');
    });
    const [isAddTemplateDialogVisible, setIsAddTemplateDialogVisible] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [currentCamera, setCurrentCamera] = useState<{
        position: [number, number, number],
        zoom: number,
        target: [number, number, number]
    } | null>(null);
    const [restoringTemplateCamera, setRestoringTemplateCamera] = useState<{
        position: [number, number, number],
        zoom: number,
        target: [number, number, number]
    } | null>(() => {
        try {
            const raw = localStorage.getItem('chartCamera');
            if (raw) {
                const val = JSON.parse(raw);
                if (val && Array.isArray(val.position) && Array.isArray(val.target)) {
                    return val;
                }
            }
        } catch (_){ }
        return null;
    });

    const [displayMoreInfos, setDisplayMoreInfos] = useState(false);

    // Persistiere die Kamera (Position/Ziel/Zoom) über Navigationswechsel
    useEffect(() => {
        if (currentCamera) {
            try {
                localStorage.setItem('chartCamera', JSON.stringify(currentCamera));
            } catch (_){ }
        }
    }, [currentCamera]);

    const mineralListData = useMemo(() => {
        if (!selectedItem) return null;
        const itemSource = selectedItem.source ?? 'xrf';
        const activeElementIds = new Set(
            elements.filter((e: any) => e.active && (e.source ?? 'xrf') === itemSource).map((e: any) => e.id)
        );
        const mineralData = [...(selectedItem.spectrum.spe_speres || [])]
            .filter((s: any) => s.value > 0 && activeElementIds.has(s.element_id))
            .sort((a: any, b: any) => b.value - a.value);
        const maxVal = mineralData.length > 0 ? mineralData[0].value : 1;
        return {itemSource, mineralData, maxVal};
    }, [selectedItem, elements]);

    const elementIdTemplate = (rowData: any) => {
        const itemSource = selectedItem?.source ?? 'xrf';
        let element = elements.find((x: any) => x.id === rowData.element_id && (x.source ?? 'xrf') === itemSource);
        if (!element) return null;

        return (
            <div className={'grid grid-nogutter py-1 legend-item flex align-items-left'}>
                <div className={'col-fixed m-auto'} style={{width: '40px'}}>
                    <div className={'right-sidebar-img'}
                         style={{
                             background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(255,255,255,0.5)), " + element.color,
                             borderColor: 'black',
                             width: '20px',
                             height: '20px',
                             border: '1px solid black',
                             borderRadius: '2px'
                         }}/>
                </div>
                <div className={'col m-auto'}>
                    <h4 className={'m-0 ml-2'} style={{fontSize: '14px'}}>{element.name}</h4>
                    <h5 className={'m-0 ml-2'} style={{fontSize: '11px', fontWeight: 'normal', color: '#666'}}>{'(' + element.id + '/' + element.shell + '/' + element.sub_shell + ')'}</h5>
                </div>
            </div>
        );
    }

    const amountTemplate = (rowData: any) => {
        const itemSource = selectedItem?.source ?? 'xrf';
        const element = elements.find((x: any) => x.id === rowData.element_id && (x.source ?? 'xrf') === itemSource);
        return (<div style={{float: 'right'}}>{rowData.value.toFixed(2).replace('.', ',') + unitSuffix(element?.unit)}</div>)
    }

    const handleApplyTemplate = (id: string) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setSelectedDrillholeIds(template.data.selectedDrillholeIds);
            const sources = template.data.dataSources
                ?? (template.data.dataSource ? [template.data.dataSource] : null);
            if (sources && sources.length > 0) dispatch(setDataSources(sources));
            dispatch(setMinMax({...minMax, factor: template.data.heightFactor}));
            if (template.data.camera) {
                setRestoringTemplateCamera(template.data.camera);
            }
            setLegendHideInactive(!!template.data.hideInactive);
            setNnOffset(template.data.nnOffset ?? 0);
            dispatch(setChartActiveOverrides(template.data.activeOverrides ?? {}));
            if (template.data.colorGradientXrf) dispatch(setColorGradientXrf(template.data.colorGradientXrf));
            if (template.data.colorGradientMinerals) dispatch(setColorGradientMinerals(template.data.colorGradientMinerals));
            if (typeof template.data.colorByElementXrf === 'boolean') dispatch(setColorByElementXrf(template.data.colorByElementXrf));
            if (typeof template.data.colorByElementMinerals === 'boolean') dispatch(setColorByElementMinerals(template.data.colorByElementMinerals));
            if (typeof template.data.boreholeWidth === 'number') setBoreholeWidth(template.data.boreholeWidth);
            setSelectedTemplateId(id);
        }
    };

    const handleSaveNewTemplate = async () => {
        if (newTemplateName.trim()) {
            const existing = templates.find(t => t.name.toLowerCase() === newTemplateName.trim().toLowerCase());
            const data = {
                selectedDrillholeIds,
                dataSources,
                heightFactor: minMax ? minMax.factor : 1,
                camera: currentCamera,
                hideInactive: legendHideInactive,
                nnOffset,
                activeOverrides: chartActiveOverrides,
                colorGradientXrf,
                colorGradientMinerals,
                colorByElementXrf,
                colorByElementMinerals,
                boreholeWidth,
            };
            if (existing) {
                await updateTemplate(existing.id, data);
                setSelectedTemplateId(existing.id);
            } else {
                const newTpl = await addTemplate(newTemplateName, data);
                if (newTpl) setSelectedTemplateId(newTpl.id);
            }
            setNewTemplateName('');
            setIsAddTemplateDialogVisible(false);
        }
    };

    const handleDeleteTemplate = () => {
        if (selectedTemplateId) {
            deleteTemplate(selectedTemplateId);
            setSelectedTemplateId(null);
        }
    };

    // Deselect if something changes
    useEffect(() => {
        if (selectedTemplateId) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                const templateSources = template.data.dataSources
                    ?? (template.data.dataSource ? [template.data.dataSource] : []);
                const isSameDrillholes = JSON.stringify(template.data.selectedDrillholeIds) === JSON.stringify(selectedDrillholeIds);
                const isSameDataSources = JSON.stringify(templateSources) === JSON.stringify(dataSources);
                const isSameHeightFactor = template.data.heightFactor === (minMax ? minMax.factor : 1);
                const isSameHideInactive = (template.data.hideInactive ?? false) === legendHideInactive;
                const isSameNnOffset = (template.data.nnOffset ?? 0) === nnOffset;
                const isSameActiveOverrides = JSON.stringify(template.data.activeOverrides ?? {}) === JSON.stringify(chartActiveOverrides);
                const isSameGradientXrf = !template.data.colorGradientXrf
                    || JSON.stringify(template.data.colorGradientXrf) === JSON.stringify(colorGradientXrf);
                const isSameGradientMinerals = !template.data.colorGradientMinerals
                    || JSON.stringify(template.data.colorGradientMinerals) === JSON.stringify(colorGradientMinerals);
                const isSameBoreholeWidth = template.data.boreholeWidth == null
                    || template.data.boreholeWidth === boreholeWidth;
                const isSameColorByElementXrf = template.data.colorByElementXrf == null
                    || template.data.colorByElementXrf === colorByElementXrf;
                const isSameColorByElementMinerals = template.data.colorByElementMinerals == null
                    || template.data.colorByElementMinerals === colorByElementMinerals;
                if (!isSameDrillholes || !isSameDataSources || !isSameHeightFactor || !isSameHideInactive || !isSameNnOffset || !isSameActiveOverrides || !isSameGradientXrf || !isSameGradientMinerals || !isSameBoreholeWidth || !isSameColorByElementXrf || !isSameColorByElementMinerals) {
                    setSelectedTemplateId(null);
                }
            }
        }
    }, [selectedDrillholeIds, dataSources, minMax, legendHideInactive, nnOffset, chartActiveOverrides, colorGradientXrf, colorGradientMinerals, colorByElementXrf, colorByElementMinerals, boreholeWidth, templates, selectedTemplateId]);

    const selectedBoreholes = useMemo(() => {
        if (!selectedBoreholesRaw) return [];
        const bhs = selectedBoreholesRaw.filter((bh: any) => selectedDrillholeIds.includes(bh.hole_id));
        const wantXrf = dataSources.includes('xrf');
        const wantMinerals = dataSources.includes('minerals');
        return bhs.map((bh: any) => {
            const xrfMea = wantXrf
                ? (bh.dri_mea ?? []).map((m: any) => ({...m, __source: 'xrf' as const}))
                : [];
            const minMea = wantMinerals
                ? (bh.dri_mea_minerals ?? []).map((m: any) => ({...m, __source: 'minerals' as const}))
                : [];
            return {
                ...bh,
                dri_mea: [...xrfMea, ...minMea],
                greatest_amount_value: Math.max(
                    wantXrf ? (bh.greatest_amount_value ?? 0) : 0,
                    wantMinerals ? (bh.greatest_amount_value_minerals ?? 0) : 0,
                ) || 1,
                greatest_amount_value_xrf: bh.greatest_amount_value ?? 0,
                greatest_amount_value_minerals: bh.greatest_amount_value_minerals ?? 0,
            };
        });
    }, [selectedBoreholesRaw, dataSources, selectedDrillholeIds]);

    // Drillholes captured by the GeoMap line (within the buffer). Empty when no line is drawn.
    const drillholeOptions = useMemo(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || offset <= 0) return [];
        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, offset / 1.6, {units: 'meters'});
        const result: { value: string; label: string }[] = [];
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                result.push({value: item.hole_id, label: item.hole_id});
            }
        }
        return result;
    }, [selectLine, withCoords, offset]);

    const dispatch = useAppDispatch();

    const targetRef: any = useRef();

    const [resetView, setResetView] = useState<boolean>(false);
    const [maxAmount, setMaxAmount] = useState<number>(0);
    const [maxAmountXrf, setMaxAmountXrf] = useState<number>(0);
    const [maxAmountMinerals, setMaxAmountMinerals] = useState<number>(0);
    const [showLoading, setShowLoading] = useState<boolean>(true);


    // Sync selectedDrillholeIds with the GeoMap line whenever the line changes.
    // Fires on first mount AND on every subsequent line change. A localStorage
    // tracker ensures manual dropdown selections survive renders where the line
    // hasn't actually changed (template apply, deselect, etc.).
    useEffect(() => {
        if (dataLoading) return;

        if (selectLine && withCoords.length > 0 && offset > 0) {
            const currentLineKey = JSON.stringify(selectLine);
            const lastSyncedKey = localStorage.getItem('chartLastSyncedSelectLine');
            if (currentLineKey === lastSyncedKey) {
                isInitialMount.current = false;
                return;
            }

            const c1 = [selectLine.x1, selectLine.y1];
            const c2 = [selectLine.x2, selectLine.y2];
            const ls = turf.lineString([c1, c2]);
            const buff = turf.buffer(ls, offset / 1.6, {units: 'meters'});
            const ids: string[] = [];
            const capturedHoles: any[] = [];
            for (const item of withCoords) {
                if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
                const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
                if (turf.booleanPointInPolygon(pt, buff)) {
                    ids.push(item.hole_id);
                    capturedHoles.push(item);
                }
            }
            setSelectedDrillholeIds(ids);
            // Re-fetch with the new line start so dripos coordinates are aligned
            // to the new line, even if the captured set is identical to before.
            if (capturedHoles.length > 0) {
                fetchDetails(capturedHoles, [selectLine.x1, selectLine.y1]);
            }
            localStorage.setItem('chartLastSyncedSelectLine', currentLineKey);
            isInitialMount.current = false;
        } else if (selectLine && withCoords.length === 0 && selectedBoreholesRaw && selectedBoreholesRaw.length > 0) {
            // selectLine vorhanden, aber withCoords noch nicht geladen — warten
        } else if (!selectLine && selectedBoreholesRaw && selectedBoreholesRaw.length > 0 && isInitialMount.current) {
            // Kein selectLine (z.B. von Boreholes3D): auto-generieren aus top-level lat/lon
            // (enriched boreholes haben kein collar-Feld, sondern bh.latitude / bh.longitude)
            // und Daten mit korrektem Offset neu laden, da dripos-Koordinaten offset-abhängig sind
            const withValidCoords = selectedBoreholesRaw.filter(
                (bh: any) => bh.longitude != null && bh.latitude != null
            );
            if (withValidCoords.length > 0) {
                const lons: number[] = withValidCoords.map((bh: any) => bh.longitude as number);
                const lats: number[] = withValidCoords.map((bh: any) => bh.latitude as number);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);
                const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
                // Gesamttiefe aus dem letzten dripos-Punkt
                const avgDepth = withValidCoords.reduce(
                    (sum: number, bh: any) => {
                        const last = bh.dri_dripos?.[bh.dri_dripos.length - 1];
                        return sum + (last?.depth ?? 100);
                    }, 0
                ) / withValidCoords.length;
                const latRad = (centerLat * Math.PI) / 180;
                const paddingDeg = (avgDepth / 2) / (111320 * Math.cos(latRad));
                const newSelectLine = {
                    x1: minLon - paddingDeg, y1: centerLat,
                    x2: maxLon + paddingDeg, y2: centerLat,
                };
                dispatch(setSelectLine(newSelectLine));
                const ids = selectedBoreholesRaw.map((bh: any) => bh.hole_id).filter(Boolean);
                setSelectedDrillholeIds(ids);
                // Re-fetch mit korrektem Offset, damit dripos-Koordinaten stimmen
                fetchDetails(selectedBoreholesRaw, [newSelectLine.x1, newSelectLine.y1]);
            }
            isInitialMount.current = false;
        } else if (!selectLine && !dataLoading && withCoords.length === 0 && isInitialMount.current) {
            isInitialMount.current = false;
        }

        if (!selectLine) {
            localStorage.removeItem('chartLastSyncedSelectLine');
        }
    }, [selectedBoreholesRaw, selectLine, withCoords, offset, dataLoading]);

    // Persistieren der Einstellungen
    useEffect(() => {
        localStorage.setItem('chartSelectedDrillholes', JSON.stringify(selectedDrillholeIds));
    }, [selectedDrillholeIds]);

    useEffect(() => {
        localStorage.setItem('chartLegendHideInactive', String(legendHideInactive));
    }, [legendHideInactive]);

    useEffect(() => {
        localStorage.setItem('chartNNOffset', String(nnOffset));
    }, [nnOffset]);
    useEffect(() => {
        localStorage.setItem('chartBoreholeWidth', String(boreholeWidth));
    }, [boreholeWidth]);

    // When a NEW line is drawn on the map, seed nnOffset with the first
    // borehole's RELATIVE LEVEL. We persist the last-seen line so re-mounting
    // with the same line preserves the user's manual nnOffset.
    useEffect(() => {
        if (!selectLine || !withCoords || withCoords.length === 0 || offset <= 0) return;
        const currentLineKey = JSON.stringify(selectLine);
        const lastSeenKey = localStorage.getItem('chartLastSeenSelectLine');
        if (currentLineKey === lastSeenKey) return;

        const c1 = [selectLine.x1, selectLine.y1];
        const c2 = [selectLine.x2, selectLine.y2];
        const ls = turf.lineString([c1, c2]);
        const buff = turf.buffer(ls, offset / 1.6, {units: 'meters'});

        let firstBorehole: any = null;
        for (const item of withCoords) {
            if (!item.collar || item.collar.LONGITUDE == null || item.collar.LATITUDE == null) continue;
            const pt = turf.point([item.collar.LONGITUDE, item.collar.LATITUDE]);
            if (turf.booleanPointInPolygon(pt, buff)) {
                firstBorehole = item;
                break;
            }
        }

        const level = firstBorehole?.collar?.['RELATIVE LEVEL'];
        if (level != null && isFinite(level)) {
            setNnOffset(level);
        }
        localStorage.setItem('chartLastSeenSelectLine', currentLineKey);
    }, [selectLine, withCoords, offset]);

    useEffect(() => {
        if (selectedTemplateId) {
            localStorage.setItem('chartSelectedTemplateId', selectedTemplateId);
        } else {
            localStorage.removeItem('chartSelectedTemplateId');
        }
    }, [selectedTemplateId]);

    // Fetch details when user picks drillholes from dropdown
    useEffect(() => {
        if (selectedDrillholeIds.length === 0 || data.length === 0) return;
        const enrichedIds = new Set((selectedBoreholesRaw || []).map((b: any) => b.hole_id));
        const missing = selectedDrillholeIds.filter(id => !enrichedIds.has(id));
        if (missing.length === 0) return;
        const allToLoad = selectedDrillholeIds
            .map(id => data.find((d: any) => d.hole_id === id))
            .filter(Boolean);

        if (allToLoad.length > 0) {
            let startCoords = selectLine ? [selectLine.x1, selectLine.y1] : undefined;

            // Simulate "click on Map" ONLY if no line exists AND only one drillhole is selected and it's a new selection
            if (!selectLine && allToLoad.length === 1 && missing.length === 1 && missing[0] === allToLoad[0].hole_id) {
                const drillhole = allToLoad[0];
                const collar = drillhole.collar;
                const hasCoords = collar && collar.LONGITUDE != null && collar.LATITUDE != null;
                const lon = hasCoords ? collar.LONGITUDE : 0;
                const lat = hasCoords ? collar.LATITUDE : 0;
                const totalDepth = collar?.TOTALDEPTH || 100;
                const latRad = (lat * Math.PI) / 180;
                const halfOffsetDeg = (totalDepth / 2) / (111320 * Math.cos(latRad));

                const newSelectLine = {
                    x1: lon - halfOffsetDeg, y1: lat,
                    x2: lon + halfOffsetDeg, y2: lat,
                };
                dispatch(setSelectLine(newSelectLine));
                startCoords = [newSelectLine.x1, newSelectLine.y1];
            }

            fetchDetails(allToLoad, startCoords);
        }
    }, [selectedDrillholeIds, data]);

    useEffect(() => {
        if (!dataLoading) {
            setShowLoading(false)
        }
    }, [dataLoading]);

    function getRandomArbitrary(min: number, max: number) {
        return Math.random() * (max - min) + min;
    }

    useEffect(() => {
        if (selectedBoreholes && selectedBoreholes.length > 0 && selectLine) {
            let maxA = 0;
            let maxXrf = 0;
            let maxMinerals = 0;

            for (const bh of selectedBoreholes) {
                if (bh.greatest_amount_value > maxA) maxA = bh.greatest_amount_value;
                if ((bh.greatest_amount_value_xrf ?? 0) > maxXrf) maxXrf = bh.greatest_amount_value_xrf ?? 0;
                if ((bh.greatest_amount_value_minerals ?? 0) > maxMinerals) maxMinerals = bh.greatest_amount_value_minerals ?? 0;
            }

            setMaxAmount(maxA);
            setMaxAmountXrf(maxXrf);
            setMaxAmountMinerals(maxMinerals);
        }
    }, [selectedBoreholes, selectLine]);

    useEffect(() => {
        if (maxAmount > 0) {
            // The active gradient is now the source of truth. We sample it
            // into a fine bucketed colorTable for the existing getColor()
            // lookups, so downstream rendering keeps working unchanged.
            const tempXrf = gradientToColorTable(colorGradientXrf, maxAmountXrf > 0 ? maxAmountXrf : maxAmount);
            const tempMin = gradientToColorTable(colorGradientMinerals, maxAmountMinerals > 0 ? maxAmountMinerals : maxAmount);
            // Fallback combined table (uses XRF gradient against the combined max)
            const tempCombined = gradientToColorTable(colorGradientXrf, maxAmount);
            dispatch(setColorTable(tempCombined));
            dispatch(setColorTableXrf(tempXrf));
            dispatch(setColorTableMinerals(tempMin));
        }
    }, [maxAmount, maxAmountXrf, maxAmountMinerals, colorGradientXrf, colorGradientMinerals]);


    return (<div id='ancorelog-old-design' className={'card w-full h-full'}>
            {showLoading && <ProgressSpinner className={'progress-spinner'}/>}
            <Dialog header="Template speichern" visible={isAddTemplateDialogVisible} style={{width: '300px'}}
                    onHide={() => setIsAddTemplateDialogVisible(false)}>
                <div className="flex flex-column gap-2">
                    <label htmlFor="templateName">Name</label>
                    <Dropdown id="templateName"
                              value={newTemplateName}
                              options={templates}
                              optionLabel="name"
                              optionValue="name"
                              onChange={(e) => setNewTemplateName(e.value)}
                              editable
                              placeholder="Name eingeben oder wählen"
                              className="p-inputtext-sm"
                              autoFocus
                    />
                </div>
                <div className="flex justify-content-end mt-4">
                    <Button label="Abbrechen" icon="pi pi-times" onClick={() => setIsAddTemplateDialogVisible(false)}
                            className="p-button-text p-button-secondary mr-2"/>
                    <Button label="Speichern" icon="pi pi-check" onClick={handleSaveNewTemplate}
                            disabled={!newTemplateName.trim()}/>
                </div>
            </Dialog>
            <div className={'relative h-full w-full'} ref={targetRef}>
                <div style={{display: 'flex', height: '100%'}}>
                    <div style={{flexShrink: 0, width: isControlBarCollapsed ? '36px' : '320px', overflow: isControlBarCollapsed ? 'hidden' : 'auto', transition: 'width 0.2s'}}>
                        <ControlBar selectedDrillhole={{}} display={[]} setDisplay={() => {
                        }} showLegend={true} legendClickable={true} legendMultiSelect={true} showMenuCard={true}
                                    showDepths={false}
                                    showScale={false}
                                    dataSources={dataSources}
                                    onDataSourcesChange={(next) => dispatch(setDataSources(next))}
                                    activeOverrides={chartActiveOverrides}
                                    onActiveOverridesChange={(next) => dispatch(setChartActiveOverrides(next))}
                                    drillholeOptions={drillholeOptions}
                                    selectedDrillholeIds={selectedDrillholeIds}
                                    onDrillholeIdsChange={setSelectedDrillholeIds}
                                    templates={templates}
                                    selectedTemplateId={selectedTemplateId}
                                    onApplyTemplate={handleApplyTemplate}
                                    onSaveTemplate={() => {
                                        const selected = templates.find(t => t.id === selectedTemplateId);
                                        setNewTemplateName(selected ? selected.name : '');
                                        setIsAddTemplateDialogVisible(true);
                                    }}
                                    onDeleteTemplate={handleDeleteTemplate}
                                    legendHideInactive={legendHideInactive}
                                    onLegendHideInactiveChange={setLegendHideInactive}
                                    menuCardContent={<div className={'menu-button w-full'}>
                                        <h4>Menu</h4>
                                        <Input edit={true} label='Height Factor' name={'heightfactor'} type={'number'}
                                               value={minMax ? minMax.factor : 1}
                                               onChange={(e) => {
                                                   if (e) {
                                                       dispatch(setMinMax({...minMax, factor: e}));
                                                   }
                                               }}/>
                                        <Input edit={true} label='Sea Level Offset (m)' name={'nnOffset'} type={'number'}
                                               value={nnOffset}
                                               onChange={(e: any) => {
                                                   const v = typeof e === 'number' ? e : Number(e);
                                                   setNnOffset(isFinite(v) ? v : 0);
                                               }}/>
                                        <Input edit={true} label='Borehole Width' name={'boreholeWidth'} type={'number'}
                                               value={boreholeWidth}
                                               onChange={(e: any) => {
                                                   const v = typeof e === 'number' ? e : Number(e);
                                                   if (isFinite(v) && v > 0) setBoreholeWidth(v);
                                               }}/>
                                        <Button label="Reset view" icon="pi pi-refresh"
                                                className="p-button-outlined p-button-sm w-full mt-2"
                                                onClick={() => setResetView(prev => !prev)}/>
                                    </div>}/>
                    </div>
                    <div style={{flex: '1 1 0', overflow: 'hidden', minWidth: 0}}>
                        <div className={'content h-full w-full'}>
                            <div className={'relative h-full grid grid-nogutter'}>
                                <div
                                    className={classNames('relative canvas-content w-full h-full')}
                                    style={{zIndex: 0,}}>
                                    <CanvasContent
                                        resetView={resetView}
                                        selectedBoreholes={selectedBoreholes}
                                        onCameraChange={setCurrentCamera}
                                        cameraState={restoringTemplateCamera}
                                        nnOffset={nnOffset}
                                        boreholeWidth={boreholeWidth}
                                    />

                                    {selectedItem && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '10px',
                                            left: '10px',
                                            zIndex: 10,
                                            width: '280px',
                                            background: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            maxHeight: 'calc(100% - 20px)'
                                        }}>
                                            {/* Header with depth */}
                                            <div style={{
                                                background: 'linear-gradient(135deg, #3b3b5c, #5a5a8a)',
                                                color: 'white',
                                                padding: '12px 16px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}>
                                                <div style={{flex: 1, minWidth: 0}}>
                                                    <div style={{fontSize: '11px', opacity: 0.8, marginBottom: '2px'}}>Depth</div>
                                                    <div style={{fontSize: '16px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                        {selectedItem.depthFrom != null
                                                            ? `${selectedItem.depthFrom.toFixed(2).replace('.', ',')} – ${selectedItem.depthTo.toFixed(2).replace('.', ',')} m`
                                                            : `${(selectedItem.spectrum.position * -1).toFixed(2).replace('.', ',')} m`}
                                                    </div>
                                                </div>
                                                <div style={{display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '8px'}}>
                                                    <button
                                                        onClick={() => setDisplayMoreInfos(true)}
                                                        style={{
                                                            background: 'rgba(255,255,255,0.2)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: '24px',
                                                            height: '24px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            lineHeight: '24px',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    ><i className="pi pi-external-link" style={{fontSize: '12px'}}/></button>
                                                    <button
                                                        onClick={() => dispatch(setSelectedItem(undefined))}
                                                        style={{
                                                            background: 'rgba(255,255,255,0.2)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: '24px',
                                                            height: '24px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            lineHeight: '24px',
                                                            padding: 0,
                                                        }}
                                                    >×</button>
                                                </div>
                                            </div>

                                            {/* Mineral list */}
                                            <div className="custom-scrollbar" style={{padding: '8px 0', overflowY: 'auto', flex: 1}}>
                                                {(() => {
                                                    if (!mineralListData) return null;
                                                    const {itemSource, mineralData, maxVal} = mineralListData;

                                                    if (mineralData.length === 0) {
                                                        return <div style={{padding: '12px 16px', color: '#999', fontSize: '13px'}}>Keine aktiven Elemente an dieser Position</div>;
                                                    }

                                                    return mineralData.map((s: any, i: number) => {
                                                        const element = elements.find((e: any) => e.id === s.element_id && (e.source ?? 'xrf') === itemSource);
                                                        const barWidth = (s.value / maxVal) * 100;
                                                        return (
                                                            <div key={i} style={{
                                                                padding: '6px 16px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                            }}>
                                                                <div style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    borderRadius: '3px',
                                                                    background: element?.color || '#999',
                                                                    flexShrink: 0,
                                                                }}/>
                                                                <div style={{flex: 1, minWidth: 0}}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        fontSize: '12px',
                                                                        marginBottom: '3px',
                                                                    }}>
                                                                        <span style={{fontWeight: 600}}>{element?.name || `#${s.element_id}`}</span>
                                                                        <span style={{color: '#666'}}>{(s.value?.toFixed(2).replace('.', ',') ?? '0,00') + unitSuffix(element?.unit)}</span>
                                                                    </div>
                                                                    <div style={{
                                                                        height: '4px',
                                                                        borderRadius: '2px',
                                                                        background: '#eee',
                                                                        overflow: 'hidden',
                                                                    }}>
                                                                        <div style={{
                                                                            height: '100%',
                                                                            width: `${barWidth}%`,
                                                                            borderRadius: '2px',
                                                                            background: element?.color || '#999',
                                                                        }}/>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {selectedItem &&
                    <Dialog
                        header={selectedItem.depthFrom != null
                            ? `Alle Werte an Position ${selectedItem.depthFrom.toFixed(2).replace('.', ',')} – ${selectedItem.depthTo.toFixed(2).replace('.', ',')} m`
                            : `Alle Werte an Position ${(selectedItem.spectrum.position * -1).toFixed(2).replace('.', ',')} m`}
                        visible={displayMoreInfos}
                        style={{width: '50vw'}}
                        onHide={() => setDisplayMoreInfos(false)}>
                        <div className='card'>
                            <DataTable value={selectedItem.spectrum.spe_speres} sortField={'value'} sortOrder={-1}>
                                <Column field='element_id' header='Element' body={elementIdTemplate} sortable/>
                                <Column field='value' header='Amount' body={amountTemplate} sortable/>
                            </DataTable>
                        </div>
                    </Dialog>
                }
            </div>
        </div>
    )
}
